/**
 * ProceduralAssets.ts — generates primitive meshes with vertex colors.
 *
 * All meshes use the MeshAsset interleaved layout (9 floats/vertex).
 * MVP shapes: plane (subdivided), box, capsule. Boats use capsule+box in stage 4.
 */

import type { AssetLoader } from "./AssetLoader";
import {
  MESH_VERTEX_FLOATS,
  MESH_VERTEX_STRIDE,
  type MeshAsset,
} from "./MeshAsset";

export class ProceduralAssetLoader implements AssetLoader {
  load(name: string): MeshAsset {
    switch (name) {
      case "unit-plane":
        return createSubdividedPlane(1, 1, 1, 1, [1, 1, 1], "unit-plane");
      default:
        throw new Error(`ProceduralAssetLoader: unknown asset "${name}"`);
    }
  }
}

/**
 * Subdivided plane on the XZ plane, centered at origin, normal +Y.
 * widthSegments × heightSegments quads.
 */
export function createSubdividedPlane(
  width: number,
  height: number,
  widthSegments: number,
  heightSegments: number,
  color: [number, number, number],
  label?: string,
): MeshAsset {
  const vx = widthSegments + 1;
  const vy = heightSegments + 1;
  const vertices = new Float32Array(vx * vy * MESH_VERTEX_FLOATS);
  const indices = new Uint32Array(widthSegments * heightSegments * 6);

  let v = 0;
  for (let j = 0; j < vy; j++) {
    const z = (j / heightSegments - 0.5) * height;
    for (let i = 0; i < vx; i++) {
      const x = (i / widthSegments - 0.5) * width;
      vertices[v++] = x;
      vertices[v++] = 0;
      vertices[v++] = z;
      vertices[v++] = 0;
      vertices[v++] = 1;
      vertices[v++] = 0;
      vertices[v++] = color[0];
      vertices[v++] = color[1];
      vertices[v++] = color[2];
    }
  }

  let k = 0;
  for (let j = 0; j < heightSegments; j++) {
    for (let i = 0; i < widthSegments; i++) {
      const a = j * vx + i;
      const b = a + 1;
      const c = a + vx;
      const d = c + 1;
      indices[k++] = a;
      indices[k++] = c;
      indices[k++] = b;
      indices[k++] = b;
      indices[k++] = c;
      indices[k++] = d;
    }
  }

  return { vertices, indices, stride: MESH_VERTEX_STRIDE, label };
}

/**
 * Capsule along +X axis, length L (cylinder portion + 2 hemispheres).
 * Returns interleaved (pos, normal, color) mesh. radial = segments around;
 * rings = hemisphere latitude segments per cap.
 */
export function createCapsuleX(
  length: number,
  radius: number,
  radial: number,
  rings: number,
  color: [number, number, number],
  label?: string,
): MeshAsset {
  const half = length * 0.5;
  // Vertex grid: rings+1 latitudes per cap, 2 caps; cylinder uses cap endpoints.
  const latCount = (rings + 1) * 2; // 0..rings = back cap, rings+1..2*rings+1 = front cap
  const lonCount = radial + 1;
  const verts: number[] = [];
  for (let li = 0; li < latCount; li++) {
    const isFrontCap = li > rings;
    const ringIdx = isFrontCap ? li - (rings + 1) : li;
    // Latitude angle in [0..π/2]; backCap ringIdx=0 → equator (centre line),
    // ringIdx=rings → pole (back tip).
    const phi = (ringIdx / rings) * (Math.PI / 2);
    const sinP = Math.sin(phi);
    const cosP = Math.cos(phi);
    const xCenter = isFrontCap ? half : -half;
    const dirX = isFrontCap ? 1 : -1;
    for (let lo = 0; lo < lonCount; lo++) {
      const theta = (lo / radial) * Math.PI * 2;
      const sinT = Math.sin(theta);
      const cosT = Math.cos(theta);
      // Sphere normal in YZ ring × X tip.
      const nx = dirX * sinP;
      const ny = cosP * cosT;
      const nz = cosP * sinT;
      const px = xCenter + nx * radius;
      const py = ny * radius;
      const pz = nz * radius;
      verts.push(px, py, pz, nx, ny, nz, color[0], color[1], color[2]);
    }
  }
  const idx: number[] = [];
  for (let li = 0; li < latCount - 1; li++) {
    // Skip the seam between the two equators (ringIdx=0 of each cap), where
    // both rings share the same X — connect them as the cylinder body.
    for (let lo = 0; lo < radial; lo++) {
      const a = li * lonCount + lo;
      const b = a + 1;
      const c = a + lonCount;
      const d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }
  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/** Axis-aligned box centred at origin. Flat-shaded (per-face normals). */
export function createBox(
  sx: number,
  sy: number,
  sz: number,
  color: [number, number, number],
  label?: string,
): MeshAsset {
  const hx = sx / 2,
    hy = sy / 2,
    hz = sz / 2;
  // 6 faces × 4 verts.
  const faces: Array<{
    n: [number, number, number];
    pts: [number, number, number][];
  }> = [
    {
      n: [+1, 0, 0],
      pts: [
        [+hx, -hy, -hz],
        [+hx, +hy, -hz],
        [+hx, +hy, +hz],
        [+hx, -hy, +hz],
      ],
    },
    {
      n: [-1, 0, 0],
      pts: [
        [-hx, -hy, +hz],
        [-hx, +hy, +hz],
        [-hx, +hy, -hz],
        [-hx, -hy, -hz],
      ],
    },
    {
      n: [0, +1, 0],
      pts: [
        [-hx, +hy, -hz],
        [+hx, +hy, -hz],
        [+hx, +hy, +hz],
        [-hx, +hy, +hz],
      ],
    },
    {
      n: [0, -1, 0],
      pts: [
        [-hx, -hy, +hz],
        [+hx, -hy, +hz],
        [+hx, -hy, -hz],
        [-hx, -hy, -hz],
      ],
    },
    {
      n: [0, 0, +1],
      pts: [
        [+hx, -hy, +hz],
        [+hx, +hy, +hz],
        [-hx, +hy, +hz],
        [-hx, -hy, +hz],
      ],
    },
    {
      n: [0, 0, -1],
      pts: [
        [-hx, -hy, -hz],
        [-hx, +hy, -hz],
        [+hx, +hy, -hz],
        [+hx, -hy, -hz],
      ],
    },
  ];
  const verts: number[] = [];
  const idx: number[] = [];
  let base = 0;
  for (const f of faces) {
    for (const p of f.pts) {
      verts.push(
        p[0],
        p[1],
        p[2],
        f.n[0],
        f.n[1],
        f.n[2],
        color[0],
        color[1],
        color[2],
      );
    }
    // CCW winding for this face when viewed from outside.
    idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
    base += 4;
  }
  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/**
 * Low-poly cone with apex at +Y, base at Y=0. `segments` controls the
 * lateral subdivision (typical: 6–8 for stylised foliage). Side faces are
 * flat-shaded; the base is closed with a triangle fan facing -Y.
 */
/**
 * Cylinder along +X axis, centred at origin, no end caps.
 * Unit length 1 (x ∈ [−0.5, +0.5]); scale via instance matrix on X only
 * to stretch lengthwise without distorting the circular cross-section.
 */
export function createCylinderX(
  radius: number,
  segments: number,
  color: [number, number, number],
  label?: string,
): MeshAsset {
  const seg = Math.max(3, Math.floor(segments));
  const verts: number[] = [];
  const idx: number[] = [];
  const half = 0.5;
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * Math.PI * 2;
    const a1 = ((i + 1) / seg) * Math.PI * 2;
    const y0 = Math.cos(a0) * radius;
    const z0 = Math.sin(a0) * radius;
    const y1 = Math.cos(a1) * radius;
    const z1 = Math.sin(a1) * radius;
    const ny = Math.cos((a0 + a1) * 0.5);
    const nz = Math.sin((a0 + a1) * 0.5);
    const base = verts.length / MESH_VERTEX_FLOATS;
    verts.push(-half, y0, z0, 0, ny, nz, color[0], color[1], color[2]);
    verts.push(+half, y0, z0, 0, ny, nz, color[0], color[1], color[2]);
    verts.push(+half, y1, z1, 0, ny, nz, color[0], color[1], color[2]);
    verts.push(-half, y1, z1, 0, ny, nz, color[0], color[1], color[2]);
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/**
 * Windsock cloth mesh along local +X. The mouth sits at X=0 and the tail
 * tapers toward +X. Colour alternates in bands along the length.
 */
export function createWindsockTubeX(
  length: number,
  mouthRadius: number,
  tailRadius: number,
  lengthSegments: number,
  radialSegments: number,
  colors: Array<[number, number, number]>,
  label?: string,
): MeshAsset {
  const lenSeg = Math.max(1, Math.floor(lengthSegments));
  const radSeg = Math.max(4, Math.floor(radialSegments));
  const verts: number[] = [];
  const idx: number[] = [];
  const slopeNx = (mouthRadius - tailRadius) / Math.max(0.001, length);

  for (let ix = 0; ix < lenSeg; ix++) {
    const x0 = (ix / lenSeg) * length;
    const x1 = ((ix + 1) / lenSeg) * length;
    const r0 = mouthRadius + (tailRadius - mouthRadius) * (ix / lenSeg);
    const r1 = mouthRadius + (tailRadius - mouthRadius) * ((ix + 1) / lenSeg);
    const color = colors[ix % colors.length] ?? [1, 0, 0];
    for (let ir = 0; ir < radSeg; ir++) {
      const a0 = (ir / radSeg) * Math.PI * 2;
      const a1 = ((ir + 1) / radSeg) * Math.PI * 2;
      const y0 = Math.cos(a0);
      const z0 = Math.sin(a0);
      const y1 = Math.cos(a1);
      const z1 = Math.sin(a1);
      const n0Len = Math.hypot(slopeNx, y0, z0);
      const n1Len = Math.hypot(slopeNx, y1, z1);
      const base = verts.length / MESH_VERTEX_FLOATS;
      verts.push(
        x0,
        y0 * r0,
        z0 * r0,
        slopeNx / n0Len,
        y0 / n0Len,
        z0 / n0Len,
        color[0],
        color[1],
        color[2],
      );
      verts.push(
        x1,
        y0 * r1,
        z0 * r1,
        slopeNx / n0Len,
        y0 / n0Len,
        z0 / n0Len,
        color[0],
        color[1],
        color[2],
      );
      verts.push(
        x1,
        y1 * r1,
        z1 * r1,
        slopeNx / n1Len,
        y1 / n1Len,
        z1 / n1Len,
        color[0],
        color[1],
        color[2],
      );
      verts.push(
        x0,
        y1 * r0,
        z1 * r0,
        slopeNx / n1Len,
        y1 / n1Len,
        z1 / n1Len,
        color[0],
        color[1],
        color[2],
      );
      idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
  }

  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/**
 * Vertical cylinder along +Y axis. Base at Y=0, top at Y=height.
 * Closed with a fan cap on top. Open at the bottom (used for piles where
 * the underside is below water and never visible).
 */
export function createCylinderY(
  radius: number,
  height: number,
  segments: number,
  color: [number, number, number],
  label?: string,
  closeBottom = false,
): MeshAsset {
  const seg = Math.max(3, Math.floor(segments));
  const verts: number[] = [];
  const idx: number[] = [];
  // Side: 4 unique verts per segment for flat-shaded normals.
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * Math.PI * 2;
    const a1 = ((i + 1) / seg) * Math.PI * 2;
    const x0 = Math.cos(a0) * radius;
    const z0 = Math.sin(a0) * radius;
    const x1 = Math.cos(a1) * radius;
    const z1 = Math.sin(a1) * radius;
    const nx = Math.cos((a0 + a1) * 0.5);
    const nz = Math.sin((a0 + a1) * 0.5);
    const base = verts.length / MESH_VERTEX_FLOATS;
    verts.push(x0, 0, z0, nx, 0, nz, color[0], color[1], color[2]);
    verts.push(x0, height, z0, nx, 0, nz, color[0], color[1], color[2]);
    verts.push(x1, height, z1, nx, 0, nz, color[0], color[1], color[2]);
    verts.push(x1, 0, z1, nx, 0, nz, color[0], color[1], color[2]);
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  // Top cap (fan facing +Y).
  const topCentre = verts.length / MESH_VERTEX_FLOATS;
  verts.push(0, height, 0, 0, 1, 0, color[0], color[1], color[2]);
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    verts.push(
      Math.cos(a) * radius,
      height,
      Math.sin(a) * radius,
      0,
      1,
      0,
      color[0],
      color[1],
      color[2],
    );
  }
  for (let i = 0; i < seg; i++) {
    const r0 = topCentre + 1 + i;
    const r1 = topCentre + 1 + ((i + 1) % seg);
    idx.push(topCentre, r0, r1);
  }
  if (closeBottom) {
    const botCentre = verts.length / MESH_VERTEX_FLOATS;
    verts.push(0, 0, 0, 0, -1, 0, color[0], color[1], color[2]);
    for (let i = 0; i < seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      verts.push(
        Math.cos(a) * radius,
        0,
        Math.sin(a) * radius,
        0,
        -1,
        0,
        color[0],
        color[1],
        color[2],
      );
    }
    for (let i = 0; i < seg; i++) {
      const r0 = botCentre + 1 + i;
      const r1 = botCentre + 1 + ((i + 1) % seg);
      idx.push(botCentre, r1, r0);
    }
  }
  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/**
 * Gable (saddle) roof — triangular prism with the ridge along +X.
 * Unit footprint: x ∈ [−0.5, +0.5] (length along ridge), z ∈ [−0.5, +0.5]
 * (eave-to-eave), y ∈ [0, 1] (eave to ridge). Scale via instance matrix
 * by (length, height, width) to fit a building OBB.
 *
 * Geometry: 6 verts (2 ridge + 4 eaves), faceted shading, no triangulated
 * eave overhang — caller adds eave by enlarging the scale slightly.
 */
export function createGableRoof(
  color: [number, number, number],
  label?: string,
): MeshAsset {
  // Unit positions.
  const v: [number, number, number][] = [
    [-0.5, 0, -0.5], // 0 eave -X -Z
    [+0.5, 0, -0.5], // 1 eave +X -Z
    [+0.5, 0, +0.5], // 2 eave +X +Z
    [-0.5, 0, +0.5], // 3 eave -X +Z
    [-0.5, 1, 0], //    4 ridge -X
    [+0.5, 1, 0], //    5 ridge +X
  ];

  // Per-face flat shading: each face has its own copy of vertices.
  const verts: number[] = [];
  const idx: number[] = [];
  let base = 0;
  const pushFace = (
    pts: [number, number, number][],
    normal: [number, number, number],
    triangulation: [number, number, number][],
  ): void => {
    for (const p of pts) {
      verts.push(
        p[0],
        p[1],
        p[2],
        normal[0],
        normal[1],
        normal[2],
        color[0],
        color[1],
        color[2],
      );
    }
    for (const [a, b, c] of triangulation) {
      idx.push(base + a, base + b, base + c);
    }
    base += pts.length;
  };

  // Bottom (looking up at roof underside is rare; include for completeness).
  pushFace(
    [v[3]!, v[2]!, v[1]!, v[0]!],
    [0, -1, 0],
    [
      [0, 1, 2],
      [0, 2, 3],
    ],
  );
  // +Z slope (front face, sloping up toward ridge).
  // Outward normal has +Y and +Z components.
  const nFront: [number, number, number] = [0, 0.8944, 0.4472]; // normalize(0,2,1)
  pushFace([v[3]!, v[2]!, v[5]!, v[4]!], nFront, [
    [0, 1, 2],
    [0, 2, 3],
  ]);
  // -Z slope (back face).
  const nBack: [number, number, number] = [0, 0.8944, -0.4472];
  pushFace([v[1]!, v[0]!, v[4]!, v[5]!], nBack, [
    [0, 1, 2],
    [0, 2, 3],
  ]);
  // +X gable end (triangle).
  pushFace([v[1]!, v[2]!, v[5]!], [1, 0, 0], [[0, 1, 2]]);
  // -X gable end (triangle).
  pushFace([v[3]!, v[0]!, v[4]!], [-1, 0, 0], [[0, 1, 2]]);

  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

export function createCone(
  radius: number,
  height: number,
  segments: number,
  color: [number, number, number],
  label?: string,
): MeshAsset {
  const seg = Math.max(3, Math.floor(segments));
  const verts: number[] = [];
  const idx: number[] = [];
  // Side: per segment we emit 3 unique vertices (apex, ring_i, ring_i+1) so
  // each face has its own flat normal.
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * Math.PI * 2;
    const a1 = ((i + 1) / seg) * Math.PI * 2;
    const x0 = Math.cos(a0) * radius;
    const z0 = Math.sin(a0) * radius;
    const x1 = Math.cos(a1) * radius;
    const z1 = Math.sin(a1) * radius;
    // Flat face normal: cross of base edge (ex,0,ez) and apex edge (-x0,h,-z0).
    const ex = x1 - x0;
    const ez = z1 - z0;
    const ax = -x0;
    const ay = height;
    const az = -z0;
    const cnx = 0 * az - ez * ay;
    const cny = ez * ax - ex * az;
    const cnz = ex * ay - 0 * ax;
    const len = Math.hypot(cnx, cny, cnz) || 1;
    const nXn = cnx / len;
    const nYn = cny / len;
    const nZn = cnz / len;
    const base = verts.length / MESH_VERTEX_FLOATS;
    verts.push(0, height, 0, nXn, nYn, nZn, color[0], color[1], color[2]);
    verts.push(x0, 0, z0, nXn, nYn, nZn, color[0], color[1], color[2]);
    verts.push(x1, 0, z1, nXn, nYn, nZn, color[0], color[1], color[2]);
    idx.push(base, base + 1, base + 2);
  }
  // Base cap (downward fan). Use one centre vertex + ring vertices with
  // normal -Y.
  const baseCentre = verts.length / MESH_VERTEX_FLOATS;
  verts.push(0, 0, 0, 0, -1, 0, color[0], color[1], color[2]);
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    verts.push(
      Math.cos(a) * radius,
      0,
      Math.sin(a) * radius,
      0,
      -1,
      0,
      color[0],
      color[1],
      color[2],
    );
  }
  for (let i = 0; i < seg; i++) {
    const r0 = baseCentre + 1 + i;
    const r1 = baseCentre + 1 + ((i + 1) % seg);
    idx.push(baseCentre, r1, r0); // CCW from below
  }
  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/**
 * Low-poly UV sphere centred at the origin. Faceted (each triangle has its
 * own normal) so it cel-shades cleanly with the rest of the scene. Use a
 * small `segs` (e.g. 10–14) for navigation marks / buoys.
 */
export function createSphere(
  radius: number,
  segs: number,
  color: [number, number, number],
  label?: string,
): MeshAsset {
  const longSeg = Math.max(4, Math.floor(segs));
  const latSeg = Math.max(3, Math.floor(segs / 2));
  const verts: number[] = [];
  const idx: number[] = [];
  // Build a (latSeg+1) × longSeg grid of points first, then emit faceted
  // triangles by duplicating their corners.
  const ring: [number, number, number][][] = [];
  for (let i = 0; i <= latSeg; i++) {
    const phi = (i / latSeg) * Math.PI; // 0..π (top to bottom)
    const y = Math.cos(phi) * radius;
    const r = Math.sin(phi) * radius;
    const row: [number, number, number][] = [];
    for (let j = 0; j < longSeg; j++) {
      const th = (j / longSeg) * Math.PI * 2;
      row.push([Math.cos(th) * r, y, Math.sin(th) * r]);
    }
    ring.push(row);
  }
  const emitTri = (
    a: [number, number, number],
    b: [number, number, number],
    c: [number, number, number],
  ): void => {
    const ux = b[0] - a[0],
      uy = b[1] - a[1],
      uz = b[2] - a[2];
    const vx = c[0] - a[0],
      vy = c[1] - a[1],
      vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const nl = Math.hypot(nx, ny, nz) || 1;
    nx /= nl;
    ny /= nl;
    nz /= nl;
    const base = verts.length / MESH_VERTEX_FLOATS;
    for (const p of [a, b, c]) {
      verts.push(p[0], p[1], p[2], nx, ny, nz, color[0], color[1], color[2]);
    }
    idx.push(base, base + 1, base + 2);
  };
  for (let i = 0; i < latSeg; i++) {
    for (let j = 0; j < longSeg; j++) {
      const j2 = (j + 1) % longSeg;
      const a = ring[i]![j]!;
      const b = ring[i]![j2]!;
      const c = ring[i + 1]![j]!;
      const d = ring[i + 1]![j2]!;
      // Skip degenerate triangles at the poles.
      if (i !== 0) emitTri(a, b, c);
      if (i !== latSeg - 1) emitTri(b, d, c);
    }
  }
  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/**
 * Faceted torus centred at the origin. The ring lies in the plane
 * perpendicular to `axis` (default "y" — flat ring in XZ plane).
 * `majorRadius` is origin→ring-centre, `minorRadius` is the tube radius.
 */
export function createTorus(
  majorRadius: number,
  minorRadius: number,
  majorSeg: number,
  minorSeg: number,
  color: [number, number, number],
  label?: string,
  axis: "x" | "y" | "z" = "y",
): MeshAsset {
  const M = Math.max(6, Math.floor(majorSeg));
  const N = Math.max(4, Math.floor(minorSeg));
  // Build (M × N) grid of points around the torus.
  const ring: [number, number, number][][] = [];
  for (let i = 0; i < M; i++) {
    const u = (i / M) * Math.PI * 2;
    const cu = Math.cos(u);
    const su = Math.sin(u);
    const row: [number, number, number][] = [];
    for (let j = 0; j < N; j++) {
      const v = (j / N) * Math.PI * 2;
      const cv = Math.cos(v);
      const sv = Math.sin(v);
      const r = majorRadius + minorRadius * cv;
      // In local (a,b,c) frame: ring lies in (a,c) plane, axis = b.
      const a = cu * r;
      const b = minorRadius * sv;
      const c = su * r;
      // Map (a,b,c) → world (x,y,z) per requested axis.
      let x: number, y: number, z: number;
      if (axis === "y") {
        x = a;
        y = b;
        z = c;
      } else if (axis === "x") {
        // axis +X: ring lies in YZ plane.
        x = b;
        y = a;
        z = c;
      } else {
        // axis +Z: ring lies in XY plane.
        x = a;
        y = c;
        z = b;
      }
      row.push([x, y, z]);
    }
    ring.push(row);
  }
  const verts: number[] = [];
  const idx: number[] = [];
  const emitTri = (
    a: [number, number, number],
    b: [number, number, number],
    c: [number, number, number],
  ): void => {
    const ux = b[0] - a[0],
      uy = b[1] - a[1],
      uz = b[2] - a[2];
    const vx = c[0] - a[0],
      vy = c[1] - a[1],
      vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const nl = Math.hypot(nx, ny, nz) || 1;
    nx /= nl;
    ny /= nl;
    nz /= nl;
    const base = verts.length / MESH_VERTEX_FLOATS;
    for (const p of [a, b, c]) {
      verts.push(p[0], p[1], p[2], nx, ny, nz, color[0], color[1], color[2]);
    }
    idx.push(base, base + 1, base + 2);
  };
  for (let i = 0; i < M; i++) {
    const i2 = (i + 1) % M;
    for (let j = 0; j < N; j++) {
      const j2 = (j + 1) % N;
      const a = ring[i]![j]!;
      const b = ring[i2]![j]!;
      const c = ring[i]![j2]!;
      const d = ring[i2]![j2]!;
      emitTri(a, b, c);
      emitTri(b, d, c);
    }
  }
  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/**
 * Mooring buoy mesh: orange unit sphere (r=1) + a darker plastic eye
 * (small torus) on top for line attachment. Caller scales the whole asset
 * via the per-instance model matrix to the buoy's real radius.
 */
export function createMooringBuoy(
  color: [number, number, number] = [0.95, 0.35, 0.08],
  label = "mooringBuoy",
): MeshAsset {
  // Body: unit sphere.
  const body = createSphere(1.0, 16, color, label + "_body");
  // Eye: small torus sitting on top of the sphere. Major radius 0.18 of body,
  // tube 0.05. Translate up so the bottom of the tube touches the sphere top.
  const eyeMajor = 0.18;
  const eyeMinor = 0.05;
  const eyeY = 1.0 + eyeMinor * 0.5;
  const eyeColor: [number, number, number] = [
    color[0] * 0.55,
    color[1] * 0.55,
    color[2] * 0.55,
  ];
  const eye = createTorus(
    eyeMajor,
    eyeMinor,
    18,
    8,
    eyeColor,
    label + "_eye",
    "x", // axis along +X → ring stands upright in YZ plane
  );
  return mergeMeshes(
    [
      { mesh: body },
      { mesh: eye, transform: (p) => [p[0], p[1] + eyeY, p[2]] },
    ],
    label,
  );
}

/**
 * Build a single merged mesh for a rectangular pile-supported dock from a
 * `DockSpec`. The mesh is generated in dock-LOCAL frame: deck centred at
 * (0,0,0), top of deck at Y=0. The caller positions the dock instance at
 * world-space (deckTopY, posX, posZ, headingY) via its model matrix.
 *
 * Composition:
 *   • Deck plank: box, centred horizontally, top at Y=0.
 *   • Piles: vertical cylinders, 2 rows along ±Z, count = pilesPerSide×2.
 *     Bottom is below water (the visible portion is from waterline down to
 *     pile cap above the deck).
 *   • Bollards: vertical cylinders rising above the deck, one per spec.
 *
 * Note this builder doesn't know about the world water level — it places
 * pile bottoms at Y = −(pileDepth + freeboard) relative to the deck top so
 * the dock instance can be translated by deckTopY directly.
 */
export function createDockMesh(
  spec: import("../sim/Dock").DockSpec,
  label = "dock",
): MeshAsset {
  const halfL = spec.length * 0.5;
  const halfW = spec.width * 0.5;
  // Deck: individual planks running ACROSS the dock (along ±Z, the short
  // axis), tiled along +X (the long axis). Each plank gets a tiny per-plank
  // colour jitter so the surface reads as real wood.
  // Wider boards + bigger gap reduce moiré/aliasing under shadows when
  // viewed from grazing angles. Real marina decking is often ~180 mm wide
  // with 8–10 mm gaps, but we exaggerate the slot so it survives downsampling.
  const PLANK_WIDTH = 0.22; // 220 mm decking
  const PLANK_GAP = 0.06; // 60 mm visible dark slot between boards
  const stride = PLANK_WIDTH + PLANK_GAP;
  const plankCount = Math.max(1, Math.floor(spec.length / stride));
  const totalSpan = plankCount * stride - PLANK_GAP;
  const startX = -totalSpan * 0.5;
  // Cheap deterministic noise per plank index.
  const noise = (i: number): number => {
    const v = Math.sin(i * 12.9898) * 43758.5453;
    return v - Math.floor(v); // [0,1)
  };
  const parts: {
    mesh: MeshAsset;
    transform?: (p: [number, number, number]) => [number, number, number];
  }[] = [];
  // Backing plate — full-deck-size dark slab sitting just below the planks.
  // Without it the gaps would let you see the sky/water through the deck;
  // the plate makes each gap read as a dark line between boards.
  const backingColor: [number, number, number] = [
    spec.deckColor[0] * 0.12,
    spec.deckColor[1] * 0.12,
    spec.deckColor[2] * 0.12,
  ];
  const backing = createBox(
    spec.length,
    spec.deckThickness * 0.4,
    spec.width,
    backingColor,
    label + "_backing",
  );
  parts.push({
    mesh: backing,
    transform: (p) => [p[0], p[1] - spec.deckThickness * 0.55, p[2]],
  });
  for (let i = 0; i < plankCount; i++) {
    const jitter = (noise(i + 1) - 0.5) * 0.18; // ±9% brightness — visible
    const plankColor: [number, number, number] = [
      Math.max(0, Math.min(1, spec.deckColor[0] + jitter)),
      Math.max(0, Math.min(1, spec.deckColor[1] + jitter * 0.9)),
      Math.max(0, Math.min(1, spec.deckColor[2] + jitter * 0.7)),
    ];
    const plank = createBox(
      PLANK_WIDTH,
      spec.deckThickness,
      spec.width - 0.01,
      plankColor,
      label + "_plank",
    );
    const xCentre = startX + i * stride + PLANK_WIDTH * 0.5;
    parts.push({
      mesh: plank,
      transform: (p) => [p[0] + xCentre, p[1] - spec.deckThickness * 0.5, p[2]],
    });
  }
  // Frame stringers along each long edge — dark slats under the planks.
  // Visible through the gaps at grazing angles.
  const stringerColor: [number, number, number] = [
    spec.deckColor[0] * 0.45,
    spec.deckColor[1] * 0.45,
    spec.deckColor[2] * 0.45,
  ];
  for (const zSign of [-1, 1] as const) {
    const stringer = createBox(
      spec.length,
      spec.deckThickness * 0.6,
      0.08,
      stringerColor,
      label + "_stringer",
    );
    const zPos = zSign * (halfW - 0.05);
    parts.push({
      mesh: stringer,
      transform: (p) => [p[0], p[1] - spec.deckThickness * 1.25, p[2] + zPos],
    });
  }
  // Piles intentionally omitted — visually we assume they are hidden under
  // the deck. Keeping the spec fields (pileDepth, pileRadius, etc.) so the
  // dock instance still positions correctly relative to the water level.
  // Bollards: dark vertical posts above the deck, one per spec.
  for (const b of spec.bollards) {
    const h = b.topAboveDeck ?? spec.bollardHeight;
    const bollard = createCylinderY(
      spec.bollardRadius,
      h,
      10,
      spec.bollardColor,
      label + "_bollard",
      true,
    );
    parts.push({
      mesh: bollard,
      transform: (p) => [p[0] + b.x, p[1], p[2] + b.z],
    });
    // Cap: a small disk slightly wider than the post for visual flair.
    const cap = createCylinderY(
      spec.bollardRadius * 1.4,
      0.04,
      10,
      spec.bollardColor,
      label + "_bollard_cap",
      true,
    );
    parts.push({
      mesh: cap,
      transform: (p) => [p[0] + b.x, p[1] + h, p[2] + b.z],
    });
  }
  // Optional gangway: a narrow plank, ramp, or staircase extending from
  // one short end of the dock toward shore. Slightly thinner than the
  // main deck.
  if (spec.gangway) {
    const g = spec.gangway;
    const gThk = spec.deckThickness * 0.85;
    const rise = g.shoreRise ?? 0;
    // Default kind: plank if shore is essentially level, otherwise pick
    // ramp for shallow rises (≤0.5 m) and stairs for taller climbs.
    const kind: "plank" | "ramp" | "stairs" =
      g.kind ??
      (Math.abs(rise) < 0.15
        ? "plank"
        : Math.abs(rise) <= 0.5
          ? "ramp"
          : "stairs");
    const offZ = g.offsetZ ?? 0;
    const s = g.side;

    if (g.steps && g.steps.length > 0) {
      // Terrain-following step profile: one box per step. Each box has
      // its base anchored 0.5 m below the dock deck so it always sits on
      // (or buries into) the slope, and its top at the requested height.
      // This avoids the staircase floating over a curved bank.
      let xLocal = halfL; // outer edge of the deck along +s direction
      const baseDrop = 0.5;
      for (const st of g.steps) {
        const cx = s * (xLocal + st.run * 0.5);
        const sy = Math.max(0.04, st.top + baseDrop);
        const cy = st.top - sy * 0.5;
        const box = createBox(
          st.run,
          sy,
          g.width,
          spec.deckColor,
          label + "_step",
        );
        parts.push({
          mesh: box,
          transform: (p) => [p[0] + cx, p[1] + cy, p[2] + offZ],
        });
        xLocal += st.run;
      }
    } else if (kind === "plank" || Math.abs(rise) < 1e-3) {
      const plank = createBox(
        g.length,
        gThk,
        g.width,
        spec.deckColor,
        label + "_gangway",
      );
      const xCentre = s * (halfL + g.length * 0.5);
      parts.push({
        mesh: plank,
        transform: (p) => [p[0] + xCentre, p[1] - gThk * 0.5, p[2] + offZ],
      });
    } else if (kind === "ramp") {
      // Tilted plank from deck edge (Y=0) to (Y=rise) at the outer end.
      const slopeLen = Math.hypot(g.length, rise);
      const theta = Math.atan2(rise, g.length); // signed
      const alpha = s * theta; // tilt outer end up when rise>0
      const plank = createBox(
        slopeLen,
        gThk,
        g.width,
        spec.deckColor,
        label + "_ramp",
      );
      // Rotate positions + normals around Z by `alpha`.
      const ca = Math.cos(alpha);
      const sa = Math.sin(alpha);
      const v = plank.vertices;
      for (let i = 0; i < v.length; i += MESH_VERTEX_FLOATS) {
        const px = v[i]!;
        const py = v[i + 1]!;
        v[i] = ca * px - sa * py;
        v[i + 1] = sa * px + ca * py;
        const nx = v[i + 3]!;
        const ny = v[i + 4]!;
        v[i + 3] = ca * nx - sa * ny;
        v[i + 4] = sa * nx + ca * ny;
      }
      // Place plank centre so the top surface midpoint goes from
      //   (s*halfL, 0)  →  (s*(halfL+length), rise).
      // Top-face normal after rotation: (-sin α, cos α). Centre sits
      // gThk/2 below the top mid-line along that normal.
      const topMidX = s * (halfL + g.length * 0.5);
      const topMidY = rise * 0.5;
      const cX = topMidX + gThk * 0.5 * sa;
      const cY = topMidY - gThk * 0.5 * ca;
      parts.push({
        mesh: plank,
        transform: (p) => [p[0] + cX, p[1] + cY, p[2] + offZ],
      });
      // Side rails (low planks along the long edges) — purely cosmetic
      // so the ramp reads as a gangway.
      const railH = 0.06;
      for (const sign of [-1, +1] as const) {
        const rail = createBox(
          slopeLen,
          railH,
          0.04,
          spec.pileColor,
          label + "_ramp_rail",
        );
        const rv = rail.vertices;
        for (let i = 0; i < rv.length; i += MESH_VERTEX_FLOATS) {
          const px = rv[i]!;
          const py = rv[i + 1]!;
          rv[i] = ca * px - sa * py;
          rv[i + 1] = sa * px + ca * py;
          const nx = rv[i + 3]!;
          const ny = rv[i + 4]!;
          rv[i + 3] = ca * nx - sa * ny;
          rv[i + 4] = sa * nx + ca * ny;
        }
        const rZ = offZ + sign * (g.width * 0.5 - 0.02);
        // Sit rail just above the top surface (its centre at gThk/2 + railH/2 above the top-line).
        const rcX = topMidX - (gThk * 0.5 + railH * 0.5) * sa;
        const rcY = topMidY + (gThk * 0.5 + railH * 0.5) * ca;
        parts.push({
          mesh: rail,
          transform: (p) => [p[0] + rcX, p[1] + rcY, p[2] + rZ],
        });
      }
    } else {
      // Stairs: stack boxes climbing from deck edge toward shore.
      const N = Math.max(1, Math.ceil(Math.abs(rise) / 0.18));
      const stepRise = rise / N; // signed
      const stepRun = g.length / N;
      for (let k = 0; k < N; k++) {
        const topY = (k + 1) * stepRise; // signed
        const sy = Math.max(0.02, Math.abs(topY));
        const cy = topY * 0.5;
        const cx = s * (halfL + (k + 0.5) * stepRun);
        const step = createBox(
          stepRun,
          sy,
          g.width,
          spec.deckColor,
          label + "_stair",
        );
        parts.push({
          mesh: step,
          transform: (p) => [p[0] + cx, p[1] + cy, p[2] + offZ],
        });
      }
    }
  }
  return mergeMeshes(parts, label);
}

/**
 * Compute the slip layout (count + stand-off from the dock edge) for a
 * mooring-styled dock. Sized for a typical 10 m yacht: slip pitch covers
 * beam + fenders, stand-off lets the LOA fit between the dock face and
 * the off-dock hardware (buoy / mooring post / y-boom tip).
 *
 * Shared by `createDockMooringMesh` (geometry) and the sim spawn code in
 * `main.ts` so buoys / posts / arms align with the visible markers.
 */
export function mooringSlipLayout(spec: import("../sim/Dock").DockSpec): {
  slipCount: number;
  standoff: number;
  slipPitch: number;
} {
  const TYPICAL_LOA = 10.0;
  const SLIP_BEAM = 4.0; // beam + fenders
  const slipPitch = SLIP_BEAM;
  const slipCount = Math.max(1, Math.floor(spec.length / slipPitch));
  const style = spec.mooringStyle ?? "dock-only";
  // Y-boom arms reach exactly to the boat's stern; buoys / posts get
  // 2 m extra so the bow line has slack to absorb wind gusts.
  const standoff = style === "y-boom" ? TYPICAL_LOA + 1.0 : TYPICAL_LOA + 2.0;
  return { slipCount, standoff, slipPitch };
}

/**
 * Auxiliary mooring infrastructure for a dock — mooring posts or y-boom
 * finger arms — depending on `spec.mooringStyle`. Built in dock-local
 * space (deck top at Y=0, length along ±X, width along ±Z); caller
 * transforms it together with the dock body.
 *
 * Buoy-style mooring is NOT built here — the caller spawns proper `Buoy`
 * sim instances (with buoyancy + ground tackle) and renders them via
 * `createMooringBuoy`. Anchor / dock-only styles need no extra geometry.
 */
export function createDockMooringMesh(
  spec: import("../sim/Dock").DockSpec,
  label = "mooring",
): MeshAsset | null {
  const style = spec.mooringStyle ?? "dock-only";
  // Y-boom arms are now driven by explicit orange (#FF8000) annotations
  // and rendered separately via `createYBoomArmMesh`. The legacy
  // per-slip auto-y-boom path is disabled to avoid duplicate geometry
  // when a magenta dock has stray orange pixels biasing the majority
  // vote toward "y-boom".
  if (style !== "mooring") return null;

  const halfL = spec.length * 0.5;
  const halfW = spec.width * 0.5;
  // Vertical offset from deck top down to water surface.
  const waterRel = -spec.freeboard;
  const layout = mooringSlipLayout(spec);
  const slipCount = layout.slipCount;
  const standoff = layout.standoff;

  const parts: {
    mesh: MeshAsset;
    transform?: (p: [number, number, number]) => [number, number, number];
  }[] = [];

  for (let side = 0; side < 2; side++) {
    const zSign = side === 0 ? -1 : 1;
    const zEdge = zSign * (halfW + standoff);
    for (let i = 0; i < slipCount; i++) {
      const t = slipCount === 1 ? 0.5 : (i + 0.5) / slipCount;
      const xCentre = -halfL + t * spec.length;

      if (style === "mooring") {
        // Wooden mooring post sticking out of the water.
        const post = createCylinderY(
          0.12,
          1.6,
          10,
          [0.42, 0.3, 0.2],
          label + "_post",
        );
        parts.push({
          mesh: post,
          transform: (p) => [
            p[0] + xCentre,
            p[1] + waterRel + 0.8 - 0.5,
            p[2] + zEdge,
          ],
        });
      } else if (style === "y-boom") {
        // Finger pier arm — narrow plank deck extending perpendicular to
        // the main dock. Same materials as the main deck.
        const armLen = standoff + 0.5; // reach right up to the dock edge
        const armW = 0.5;
        const armThk = spec.deckThickness;
        const arm = createBox(
          armW,
          armThk,
          armLen,
          spec.deckColor,
          label + "_arm",
        );
        const zCentre = zSign * (halfW + armLen * 0.5);
        parts.push({
          mesh: arm,
          transform: (p) => [
            p[0] + xCentre,
            p[1] - armThk * 0.5,
            p[2] + zCentre,
          ],
        });
        // Floating support pile under the far end.
        const pileH = spec.pileDepth + spec.freeboard;
        const pile = createCylinderY(
          spec.pileRadius * 0.7,
          pileH,
          8,
          spec.pileColor,
          label + "_arm_pile",
        );
        const zPile = zSign * (halfW + armLen);
        parts.push({
          mesh: pile,
          transform: (p) => [p[0] + xCentre, p[1] - pileH * 0.5, p[2] + zPile],
        });
      }
    }
  }

  if (parts.length === 0) return null;
  return mergeMeshes(parts, label);
}

/**
 * Standalone Y-boom finger arm — a narrow plank deck with a support
 * pile at its far end. Built in arm-local space: the arm extends along
 * +X from x=0 (root, attached to a magenta pomost edge) to x=+length
 * (tip, supported by the pile). Deck top sits at Y=0; caller applies
 * the dock's `deckTopY` translation.
 *
 * Materials match the default dock spec so the arm visually reads as
 * "part of the marina" rather than free-floating debris.
 */
export function createYBoomArmMesh(
  length: number,
  options: {
    width?: number;
    deckThickness?: number;
    deckColor?: [number, number, number];
    pileRadius?: number;
    pileHeight?: number;
    pileColor?: [number, number, number];
    label?: string;
  } = {},
): MeshAsset {
  const width = options.width ?? 0.5;
  const deckThickness = options.deckThickness ?? 0.18;
  const deckColor = options.deckColor ?? [0.55, 0.42, 0.27];
  const pileRadius = options.pileRadius ?? 0.07;
  const pileHeight = options.pileHeight ?? 1.4;
  const pileColor = options.pileColor ?? [0.36, 0.26, 0.18];
  const label = options.label ?? "y_boom_arm";

  // createBox is centred on origin → translate by +length/2 along X so
  // the root sits at x=0.
  const deck = createBox(length, deckThickness, width, deckColor, label);
  const parts: {
    mesh: MeshAsset;
    transform?: (p: [number, number, number]) => [number, number, number];
  }[] = [
    {
      mesh: deck,
      transform: (p) => [p[0] + length * 0.5, p[1] - deckThickness * 0.5, p[2]],
    },
  ];

  const pile = createCylinderY(
    pileRadius,
    pileHeight,
    8,
    pileColor,
    label + "_pile",
  );
  parts.push({
    mesh: pile,
    transform: (p) => [p[0] + length, p[1] - pileHeight * 0.5, p[2]],
  });

  return mergeMeshes(parts, label);
}

/**
 * Concatenate several meshes with optional per-mesh model matrix into one
 * MeshAsset. Useful for building boats out of capsule + box + mast.
 */
export function mergeMeshes(
  parts: {
    mesh: MeshAsset;
    transform?: (p: [number, number, number]) => [number, number, number];
  }[],
  label?: string,
): MeshAsset {
  let vCount = 0;
  let iCount = 0;
  for (const p of parts) {
    vCount += p.mesh.vertices.length / MESH_VERTEX_FLOATS;
    iCount += p.mesh.indices.length;
  }
  const verts = new Float32Array(vCount * MESH_VERTEX_FLOATS);
  const idx = new Uint32Array(iCount);
  let vBase = 0;
  let iBase = 0;
  for (const p of parts) {
    const m = p.mesh;
    const vc = m.vertices.length / MESH_VERTEX_FLOATS;
    for (let i = 0; i < vc; i++) {
      const o = i * MESH_VERTEX_FLOATS;
      const w = (vBase + i) * MESH_VERTEX_FLOATS;
      let px = m.vertices[o]!,
        py = m.vertices[o + 1]!,
        pz = m.vertices[o + 2]!;
      if (p.transform) {
        const t = p.transform([px, py, pz]);
        px = t[0];
        py = t[1];
        pz = t[2];
      }
      verts[w] = px;
      verts[w + 1] = py;
      verts[w + 2] = pz;
      verts[w + 3] = m.vertices[o + 3]!;
      verts[w + 4] = m.vertices[o + 4]!;
      verts[w + 5] = m.vertices[o + 5]!;
      verts[w + 6] = m.vertices[o + 6]!;
      verts[w + 7] = m.vertices[o + 7]!;
      verts[w + 8] = m.vertices[o + 8]!;
    }
    for (let i = 0; i < m.indices.length; i++) {
      idx[iBase + i] = m.indices[i]! + vBase;
    }
    vBase += vc;
    iBase += m.indices.length;
  }
  return { vertices: verts, indices: idx, stride: MESH_VERTEX_STRIDE, label };
}

// =====================================================================
// Stylised low-poly trees (unit space: height=1, base radius=1)
// =====================================================================

/**
 * Tiered conifer: small brown trunk + N stacked cones of decreasing radius.
 * Output mesh is normalised so caller can scale uniformly by desired height.
 */
export function createConiferTree(
  trunkColor: [number, number, number],
  foliageColor: [number, number, number],
  label = "coniferTree",
): MeshAsset {
  const segments = 6;
  // Trunk: short box at base, height = 0.18.
  const trunkH = 0.18;
  const trunkR = 0.1;
  const trunk = createBox(trunkR * 2, trunkH, trunkR * 2, trunkColor, "trunk");
  // Three cone tiers. Each tier defined by (baseY, height, radius).
  const tiers: Array<[number, number, number]> = [
    [trunkH * 0.55, 0.5, 1.0], // bottom — widest
    [0.45, 0.4, 0.78],
    [0.7, 0.32, 0.55],
  ];
  const parts: {
    mesh: MeshAsset;
    transform?: (p: [number, number, number]) => [number, number, number];
  }[] = [
    {
      mesh: trunk,
      // Trunk box centred at trunkH/2 (createBox is centred on origin).
      transform: ([x, y, z]) => [x, y + trunkH * 0.5, z],
    },
  ];
  for (const [by, h, r] of tiers) {
    const cone = createCone(1, 1, segments, foliageColor, "coniferTier");
    parts.push({
      mesh: cone,
      transform: ([x, y, z]) => [x * r, y * h + by, z * r],
    });
  }
  return mergeMeshes(parts, label);
}

/**
 * Deciduous tree: tall trunk + cluster of 4-5 bipyramid "blobs" near the top
 * to fake a rounded crown. Bipyramid = two cones back-to-back; gives a 12-tri
 * blob that reads as a foliage clump in the low-poly style.
 */
export function createDeciduousTree(
  trunkColor: [number, number, number],
  foliageColor: [number, number, number],
  label = "deciduousTree",
): MeshAsset {
  const trunkH = 0.5;
  const trunkR = 0.07;
  const trunk = createBox(trunkR * 2, trunkH, trunkR * 2, trunkColor, "trunk");
  const parts: {
    mesh: MeshAsset;
    transform?: (p: [number, number, number]) => [number, number, number];
  }[] = [
    {
      mesh: trunk,
      transform: ([x, y, z]) => [x, y + trunkH * 0.5, z],
    },
  ];
  // Build a single blob mesh once, instance with offsets.
  const blob = ((): MeshAsset => {
    const seg = 6;
    const top = createCone(1, 0.55, seg, foliageColor, "blobTop");
    const bot = createCone(1, 0.45, seg, foliageColor, "blobBot");
    return mergeMeshes(
      [
        { mesh: top },
        // Flip bottom cone (apex down) by negating Y and shifting up so the
        // base meets at y=0.
        { mesh: bot, transform: ([x, y, z]) => [x, -y, z] },
      ],
      "blob",
    );
  })();
  // Crown clumps. (cx, cy, cz, r) in unit space.
  const clumps: Array<[number, number, number, number]> = [
    [0.0, 0.55, 0.0, 0.42], // central
    [0.32, 0.7, 0.0, 0.3],
    [-0.28, 0.65, 0.18, 0.32],
    [0.05, 0.85, -0.2, 0.28],
    [-0.1, 0.5, -0.3, 0.3],
  ];
  for (const [cx, cy, cz, r] of clumps) {
    parts.push({
      mesh: blob,
      transform: ([x, y, z]) => [x * r + cx, y * r + cy, z * r + cz],
    });
  }
  return mergeMeshes(parts, label);
}

// =====================================================================
// Lofted boat hull
// =====================================================================

export interface HullLoftSpec {
  /** "sailboat" or "houseboat" — controls cross-section and plan-view shape. */
  type: "sailboat" | "houseboat";
  /** Length overall (m). Hull spans X ∈ [-length/2, +length/2]. */
  length: number;
  /** Beam (m). Maximum width at the waterline. */
  beam: number;
  /** Freeboard (m): distance from WL up to the deck edge. */
  freeboard: number;
  /** Canoe-body draft (m): distance from WL down to the lowest hull point. */
  draft: number;
  /** Hull color. */
  color: [number, number, number];
  /** Optional separate deck/topsides color. Defaults to `color`. */
  deckColor?: [number, number, number];
  /** Stations along length. More = smoother bow/stern curvature. Default 18. */
  stations?: number;
  /** Points per cross-section half (port). Default 6. */
  ringPoints?: number;
}

/**
 * Generate a lofted boat hull — pointed bow, transom stern, real underwater
 * shape. Body coordinate convention:
 *   +X forward (bow), +Y up, +Z starboard. Origin (0,0,0) = midship at WL.
 *
 * Sailboat: V-bottom canoe body, fine bow entry, narrow reverse-counter stern.
 * Houseboat: shallow flat-bottom barge, blunt rounded bow, full-beam transom.
 */
export function createHullLoft(spec: HullLoftSpec, label = "hull"): MeshAsset {
  const N = spec.stations ?? 18;
  const K = spec.ringPoints ?? 6;
  const L = spec.length;
  const halfL = L * 0.5;
  const B = spec.beam;
  const halfB = B * 0.5;
  const FB = spec.freeboard;
  const T = spec.draft;
  const isSail = spec.type === "sailboat";
  const deckColor = spec.deckColor ?? spec.color;

  /** Plan-view beam factor at length-parameter t ∈ [0=stern, 1=bow]. */
  const beamFactor = (t: number): number => {
    if (isSail) {
      // Pointed bow, gentle stern taper to a small transom (~30% beam).
      const bow = smoothstep(1.0, 0.78, t); // 1 at midship, 0 at bow tip
      const stern = lerp(0.32, 1.0, smoothstep(0.0, 0.35, t));
      return Math.min(bow, stern);
    } else {
      // Houseboat: nearly constant; small bow rounding, full transom.
      const bow = smoothstep(1.0, 0.85, t);
      const bowSoft = lerp(0.55, 1.0, bow);
      // Stern: full beam.
      const stern = smoothstep(0.0, 0.06, t); // step up quickly from 0 to 1
      return Math.min(bowSoft, lerp(0.85, 1.0, stern));
    }
  };

  /** Bottom-line depth factor at length-parameter t (1 = full draft). */
  const draftFactor = (t: number): number => {
    if (isSail) {
      // Bow rises gently above keel near tip; stern slightly raked.
      const bow = lerp(0.55, 1.0, smoothstep(0.95, 0.55, t));
      const stern = lerp(0.78, 1.0, smoothstep(0.0, 0.25, t));
      return Math.min(bow, stern);
    } else {
      // Flat bottom houseboat: full draft except near bow rocker.
      const bow = lerp(0.55, 1.0, smoothstep(0.93, 0.7, t));
      return bow;
    }
  };

  /**
   * Cross-section: emit K+1 (z, y) points from deck edge (s=0) down to
   * keel centerline (s=1). Multiplied by `wf` (beam factor) and `df`
   * (draft factor) afterwards.
   */
  const section = (s: number): { z: number; y: number } => {
    if (isSail) {
      // V-bottom with rounded bilge near WL, full beam at WL, slight tumblehome.
      const z = halfB * (1 - Math.pow(s, 1.7)) * (1 - 0.04 * (1 - s));
      // y goes from +freeboard down to -draft, faster drop initially.
      const y = FB - (FB + T) * Math.pow(s, 1.4);
      return { z, y };
    } else {
      // Flat bottom + vertical sides + radiused chine at s≈0.85.
      let z: number;
      if (s < 0.85) {
        z = halfB; // vertical side
      } else {
        const u = (s - 0.85) / 0.15;
        z = halfB * (1 - u * u); // quarter-circle chine
      }
      const y = FB - (FB + T) * s;
      return { z, y };
    }
  };

  // ---- Generate stations ------------------------------------------------
  // Per station: array of {x, y, z} for K+1 points on port half.
  // Mirror to starboard later.
  const stationsXYZ: { x: number; y: number; z: number }[][] = [];
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1); // 0=stern, 1=bow
    const x = -halfL + t * L;
    const wf = beamFactor(t);
    const df = draftFactor(t);
    const ring: { x: number; y: number; z: number }[] = [];
    for (let k = 0; k <= K; k++) {
      const s = k / K;
      const { z, y } = section(s);
      // Apply taper. The y above WL stays the same (deck height); below WL
      // we scale by draft factor so the keel rises near bow/stern.
      const yOut = y >= 0 ? y : y * df;
      ring.push({ x, y: yOut, z: -z * wf }); // port: negative Z
    }
    stationsXYZ.push(ring);
  }

  // ---- Build vertex buffer ----------------------------------------------
  // We'll produce a doubled ring (port + starboard) per station and stitch
  // strips between adjacent stations. We also cap bow/stern + add deck.
  // Vertex layout: pos(3), normal(3), color(3) = 9 floats.
  const verts: number[] = [];
  const idx: number[] = [];

  /** Compute face normal of triangle (a, b, c) in CCW order. */
  const normal3 = (
    a: [number, number, number],
    b: [number, number, number],
    c: [number, number, number],
  ): [number, number, number] => {
    const ux = b[0] - a[0],
      uy = b[1] - a[1],
      uz = b[2] - a[2];
    const vx = c[0] - a[0],
      vy = c[1] - a[1],
      vz = c[2] - a[2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const m = Math.hypot(nx, ny, nz) || 1;
    return [nx / m, ny / m, nz / m];
  };

  /** Append one vertex; returns its index. */
  const pushVertex = (
    p: [number, number, number],
    n: [number, number, number],
    c: [number, number, number],
  ): number => {
    const i = verts.length / MESH_VERTEX_FLOATS;
    verts.push(p[0], p[1], p[2], n[0], n[1], n[2], c[0], c[1], c[2]);
    return i;
  };

  /** Add a quad as two triangles with flat shading. CCW from outside. */
  const addQuad = (
    p00: [number, number, number],
    p10: [number, number, number],
    p11: [number, number, number],
    p01: [number, number, number],
    color: [number, number, number],
  ): void => {
    const n1 = normal3(p00, p10, p11);
    const a = pushVertex(p00, n1, color);
    const b = pushVertex(p10, n1, color);
    const c = pushVertex(p11, n1, color);
    const d = pushVertex(p01, n1, color);
    idx.push(a, b, c, a, c, d);
  };

  /** Add a triangle with flat shading. */
  const addTri = (
    p0: [number, number, number],
    p1: [number, number, number],
    p2: [number, number, number],
    color: [number, number, number],
  ): void => {
    const n = normal3(p0, p1, p2);
    const a = pushVertex(p0, n, color);
    const b = pushVertex(p1, n, color);
    const c = pushVertex(p2, n, color);
    idx.push(a, b, c);
  };

  // ---- Side strips (port + starboard) -----------------------------------
  const portColor = spec.color;
  for (let i = 0; i < N - 1; i++) {
    const s0 = stationsXYZ[i]!;
    const s1 = stationsXYZ[i + 1]!;
    for (let k = 0; k < K; k++) {
      const a = s0[k]!;
      const b = s1[k]!;
      const c = s1[k + 1]!;
      const d = s0[k + 1]!;
      // Port side. CCW from outside (looking at port from -Z).
      addQuad(
        [a.x, a.y, a.z],
        [d.x, d.y, d.z],
        [c.x, c.y, c.z],
        [b.x, b.y, b.z],
        portColor,
      );
      // Starboard mirror (z flipped). CCW from outside (+Z).
      addQuad(
        [a.x, a.y, -a.z],
        [b.x, b.y, -b.z],
        [c.x, c.y, -c.z],
        [d.x, d.y, -d.z],
        portColor,
      );
    }
  }

  // ---- Centerline (keel) seam -------------------------------------------
  // Connect port keel point (last in ring) with starboard mirror across
  // adjacent stations to close the bottom of the V/flat.
  for (let i = 0; i < N - 1; i++) {
    const a = stationsXYZ[i]![K]!;
    const b = stationsXYZ[i + 1]![K]!;
    // a.z and b.z are 0 (or near-zero) at the keel; if exactly 0 the seam is
    // already shared. With sailboat the K-th point may have small non-zero z
    // due to the side curve — close it explicitly here.
    if (Math.abs(a.z) < 1e-4 && Math.abs(b.z) < 1e-4) continue;
    addQuad(
      [a.x, a.y, a.z],
      [b.x, b.y, b.z],
      [b.x, b.y, -b.z],
      [a.x, a.y, -a.z],
      portColor,
    );
  }

  // ---- Bow cap -----------------------------------------------------------
  // Last station (i=N-1) is the bow. Its width factor ~0 collapses the ring
  // to a (near-)point. To avoid degenerate triangles in the side strip we
  // simply connected the small ring above; if any width remains, cap with
  // a fan to the bow tip.
  // For simplicity we already produced quads; bow ring will be tiny but
  // closed by the quad strips. No additional cap needed.

  // ---- Stern transom -----------------------------------------------------
  // First station (i=0) has non-zero beam (transom). Triangulate a flat
  // polygon facing -X. Take the port ring + mirrored starboard.
  {
    const ring = stationsXYZ[0]!;
    // Build a closed polygon: deck-port (k=0) → keel (k=K) → mirrored back to deck-stbd.
    const poly: [number, number, number][] = [];
    for (let k = 0; k <= K; k++) {
      const p = ring[k]!;
      poly.push([p.x, p.y, p.z]);
    }
    for (let k = K - 1; k >= 0; k--) {
      const p = ring[k]!;
      poly.push([p.x, p.y, -p.z]);
    }
    // Triangulate as fan from poly[0]. For a slightly concave transom a fan
    // is fine because the polygon is monotonic in y.
    for (let k = 1; k < poly.length - 1; k++) {
      addTri(poly[0]!, poly[k + 1]!, poly[k]!, portColor); // outward normal -X
    }
  }

  // ---- Deck cap ---------------------------------------------------------
  // Use the first ring point of every station (k=0, deck edge) as the deck
  // outline. Triangulate as a strip between port and starboard.
  for (let i = 0; i < N - 1; i++) {
    const a = stationsXYZ[i]![0]!; // port deck edge
    const b = stationsXYZ[i + 1]![0]!;
    addQuad(
      [a.x, a.y, a.z],
      [a.x, a.y, -a.z],
      [b.x, b.y, -b.z],
      [b.x, b.y, b.z],
      deckColor,
    );
  }

  return {
    vertices: new Float32Array(verts),
    indices: new Uint32Array(idx),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
